import { useCallback, useContext } from 'react';
import { PapiDeviation } from '@wix/da-papi-types';
import { useDispatch } from 'react-redux';
import { MatureDeviationContext } from '../../../Context/MatureDeviationContext';
import { useSubscriptionTier } from '../../../utils/hooks/useSubscriptionTier';
import { normalizePremiumFolderData } from '../../../utils/normalizePremiumFolderData';
import { overrideSubscribeMatureFilter } from '../../../Tier/redux/consumer/actions';
import { DeviationTypes } from '../../../types/deviation';
import { getDeviationType, getShouldDisplayPaywall } from '../../_helpers';

/** Returns booleans that may indicate that a deviation isn't viewable including:
 *  subscription, premium galleries, or when blocked by mature filter (and returns
 *  the function which can override that block on a case-by-case basis).
 *
 *  Includes a `hasBlock` which is true if blocked by any of the above reasons.
 */
export function useViewBlockChecks(deviation: PapiDeviation): {
  hasBlock: boolean;
  isBlockedBySubscription: boolean;
  isBlockedByPremium: boolean;
  isBlockedByMature: boolean;
  overrideMatureFilter: () => void;
} {
  const dispatch = useDispatch();
  const deviationType = getDeviationType(deviation);

  const { isSubscribedButBlocked, needsSubscription } =
    useSubscriptionTier(deviation);
  const isBlockedBySubscription =
    (needsSubscription || isSubscribedButBlocked) &&
    getShouldDisplayPaywall(deviationType);

  const { isPremiumLocked: isBlockedByPremium } = normalizePremiumFolderData(
    deviation?.premiumFolderData
  );

  const {
    showBlocked: isBlockedByMature,
    overrideMatureFilter: overrideMatureFilterUnwrapped,
  } = useContext(MatureDeviationContext);
  const overrideMatureFilter = useCallback(() => {
    if (deviationType === DeviationTypes.TIER) {
      dispatch(overrideSubscribeMatureFilter(deviation));
    }
    overrideMatureFilterUnwrapped();
  }, [overrideMatureFilterUnwrapped, dispatch, deviation, deviationType]);

  return {
    hasBlock:
      isBlockedBySubscription || isBlockedByPremium || isBlockedByMature,
    isBlockedBySubscription,
    isBlockedByPremium,
    isBlockedByMature,
    overrideMatureFilter,
  };
}
